import { Box, Flex, Image, Stack, Text, useMantineTheme } from "@mantine/core";
import type { ReactNode } from "react";

interface AuthLayoutProps {
  children: ReactNode;
  subtitle?: string;
  heroSubtitle?: string;
  heroImageUrl?: string;
  schoolName?: string;
  schoolLogo?: string;
}

export function AuthLayout({
  children,
  subtitle = "Access your RO Home dashboard",
  heroSubtitle = "Your gateway to simplified home management",
  heroImageUrl = "/assets/images/robg.jpg",
  schoolName,
  schoolLogo,
}: AuthLayoutProps) {
  const theme = useMantineTheme();
  const paddingValue = Number(theme.spacing.xl) * 2;

  return (
    <Flex
      direction={{ base: "column", md: "row" }}
      style={{
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* Hero Image Section - Left side on desktop, top on mobile */}
      <Box
        style={{
          flex: "1 1 66%",
          position: "relative",
          minHeight: "40vh",
          backgroundColor: theme.colors.blue[7],
        }}
      >
        <Image
          src={heroImageUrl}
          alt="RO Home"
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />

        <Flex
          direction="column"
          align="center"
          justify="center"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            padding: "0 20px",
            boxSizing: "border-box"
          }}
        >
          <Stack align="center" gap="xl">
            {schoolLogo ? (
              <Flex align="center" gap="md">
                <Image
                  src="/assets/images/RO_FullLogo.png"
                  alt="RO Home Logo"
                  w={250}
                />
                <Text size="xl" fw={500} c="white">
                  ×
                </Text>
                <Image
                  src={schoolLogo}
                  alt={`${schoolName} Logo`}
                  h={60}
                  fit="contain"
                />
              </Flex>
            ) : (
              <Image
                src="/assets/images/RO_FullLogo.png"
                alt="RO Home Logo"
                w={250}
              />
            )}
            <Text style={{ color: "white" }} size="lg" ta="center">
              {heroSubtitle}
            </Text>
          </Stack>
        </Flex>
      </Box>

      {/* Content Section - Right side on desktop, bottom on mobile */}
      <Flex
        justify="center"
        align="center"
        style={{
          flex: "1 1 33%",
          padding: `${paddingValue}px`,
        }}
      >
        <Box style={{ width: "100%", maxWidth: "450px" }}>
          <Stack align="center" gap="xl">
            {subtitle && (
              <Text size="md" c="dimmed" ta="center" maw={400}>
                {subtitle}
              </Text>
            )}
            {children}
          </Stack>
        </Box>
      </Flex>
    </Flex>
  );
}
